import { Ajax } from "./api/infra";
import { BootstrapStyle, UsecaseAction, UsecaseActionType, UserSession } from "./model";

import * as actionExecutorCore from "./ActionExecutor_index";

/**
 * Helper class for executing usecase actions.
 */
export class ActionExecutor {

  /** The action that is being executed. */
  public action: UsecaseAction;

  /** Result from the last execution of the current action. */
  public result: any;

  /** Status of the result */
  public status: BootstrapStyle;

  constructor(action: UsecaseAction) {
    this.action = action;
  }

  /** Returns true if the execution is asynchronous. Only asynchronous calls may make http requests to external resources. */
  public get isAsync(): boolean {
    // Note: From browser, UsecaseActionType.ServerScript is async. When server environment is done, then this must be handled depending on the environment.
    return this.action.actionType != UsecaseActionType.ClientScript;
  }

  /**
   * Executes the current action.
   * !! IMPORTANT !! As of now, the method only supports client-side browser execution. Server-side / Node execution is not yet supported.
   * @param item The current item that the script processes.
   * @param session The current user session.
   * @param ajax Ajax object for HTTP calls.
   */
  public execute(item: any, session: UserSession, ajax: Ajax): Promise<boolean> {
    switch (this.action.actionType) {
      case UsecaseActionType.ClientScript:
        return Promise.resolve(this.executeSync(item, session));
      case UsecaseActionType.ClientScriptAsync:
        return this.executeAsync(item, session, ajax);
      default:
        throw new Error(`Execution of action type ${this.action.actionType} is not yet supported.`);
    }
  }

  /**
   * Executes the current action as synchronous client-side (browser) code.
   * !! IMPORTANT !! As of now, the method only supports client-side browser execution. Server-side / Node execution is not yet supported.
   * @param item The current item that the script processes.
   * @param session The current user session.
   */
  public executeSync(item: any, session: UserSession): boolean {
    if (!this.action.content) {
      this.status = BootstrapStyle.Danger;
      this.result = "Ei skriptiä\n\nValitse skripti, jossa on ajettava koodia.";
      return false;
    }
    const code = this.action.content;
    this.status = BootstrapStyle.Primary;
    this.result = "Skriptiä ajetaan...";

    const funcBuilder = (code) => {
      return Function(`"use strict";return (function(item, session, core){` + code + `})`)()(
        item, session, actionExecutorCore
      );
    };

    try {
      this.result = funcBuilder(code) || "Skripti ajettu: Palautettu arvo on tyhjä.";
      this.status = BootstrapStyle.Success;
      return true;
    }
    catch (ex) {
      this.result = ex.toString();
      this.status = BootstrapStyle.Danger;
      console.error(ex);
      return false;
    }
  }

  /**
   * Executes the current action as asynchronous client-side (browser) code.
   * !! IMPORTANT !! As of now, the method only supports client-side browser execution. Server-side / Node execution is not yet supported.
   * @param item The current item that the script processes.
   * @param session The current user session.
   * @param ajax Ajax object for HTTP calls.
   */
  public executeAsync(item: any, session: UserSession, ajax: Ajax): Promise<boolean> {
    if (!this.action.content) {
      this.status = BootstrapStyle.Danger;
      this.result = "Ei skriptiä\n\nValitse skripti, jossa on ajettava koodia.";
      return Promise.resolve(false);
    }
    const code = this.action.content;
    this.status = BootstrapStyle.Primary;
    this.result = "Skriptiä ajetaan...";

    const funcBuilder = (code) => {
      return Function(`"use strict";return (async function(item, session, ajax, core){` + code + `})`)()(
        item, session, ajax, actionExecutorCore
      );
    };

    try {
      (funcBuilder(code) as Promise<any>).then((result) => {
        this.result = result || "Skripti ajettu: Palautettu arvo on tyhjä.";
        this.status = BootstrapStyle.Success;
        return true;
      }).catch((error) => {
        this.result = error?.toString() || "Tuntematon virhe!";
        this.status = BootstrapStyle.Danger;
        return false;
      });
    }
    catch (ex) {
      this.status = BootstrapStyle.Danger;
      this.result = ex.toString();
      return Promise.resolve(false);
    }
  }

}